import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UnauthorizedPage } from './UnauthorizedPage';
import { hasScreenPermission } from '../lib/permissions';
import LoadingState from './LoadingState';

interface PermissionRouteProps {
  children: React.ReactNode;
  requiredPermission?: {
    screen: string;
    type?: 'view' | 'create' | 'edit' | 'delete';
  };
  requireAdmin?: boolean;
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  requiredPermission,
  requireAdmin = false
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <LoadingState />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  if (requireAdmin && user.role !== 'admin') {
    return <UnauthorizedPage message="This page is only accessible to administrators" />;
  }

  if (requiredPermission && !requireAdmin) {
    const { screen, type = 'view' } = requiredPermission;

    if (user.role === 'admin') {
      return <>{children}</>;
    }

    if (!hasScreenPermission(user, screen, type)) {
      return (
        <UnauthorizedPage
          message={`You don't have ${type} permission for this area.`}
        />
      );
    }
  }

  return <>{children}</>;
};
